import { useMemo } from 'react';
import { DAYS_SHORT, getActiveDaysForRow } from '../lib/constants';

/**
 * CalendarPreview
 *
 * mode='abstract' — 5-row grid (Sun–Sat).
 *   Rows 0-3 = 1st–4th week.  Row 4 = "Last" week (distinct from 4th).
 *   nth=-1 highlights ONLY row 4; nth=4 highlights ONLY row 3.
 *
 * mode='real' — standard monthly calendar tied to year/month props.
 */
export default function CalendarPreview({
  selectedRows = [],
  hoveredRow   = null,
  mode         = 'abstract',
  year,
  month,
  dimWeekends  = false,
}) {
  const today = new Date();

  // ── Abstract mode ──────────────────────────────────────────────────────────
  // 5 rows: weeks 0-3 = 1st-4th, week 4 = "Last"
  const abstractCells = useMemo(() => {
    const cells = [];
    for (let week = 0; week < 5; week++) {
      for (let dow = 0; dow < 7; dow++) {
        cells.push({ week, dow });
      }
    }
    return cells;
  }, []);

  const ROW_LABELS = ['1st', '2nd', '3rd', '4th', 'Last'];

  // Map nth value to abstract row index
  // n=1→0, n=2→1, n=3→2, n=4→3, n=-1→4 (DISTINCT from n=4)
  const nthToWeekIdx = n => (n === -1 ? 4 : n - 1);

  const abstractActiveSet = (rows) => {
    const active = new Set(); // keys: "week-dow"
    rows.forEach(row => {
      if (!row) return;
      if (!row.nth) {
        // Simple day-of-week: highlight that DOW in rows 0-3 (not the Last row)
        for (let w = 0; w < 4; w++) {
          row.days.forEach(dow => active.add(w + '-' + dow));
        }
      } else {
        row.nth.forEach(n => {
          const weekIdx = nthToWeekIdx(n);
          if (weekIdx >= 0 && weekIdx <= 4) {
            row.days.forEach(dow => active.add(weekIdx + '-' + dow));
          }
        });
      }
    });
    return active;
  };

  const activeSel = useMemo(() => abstractActiveSet(selectedRows), [selectedRows]);
  const activeHov = useMemo(() => hoveredRow ? abstractActiveSet([hoveredRow]) : new Set(), [hoveredRow]);

  // ── Real mode ──────────────────────────────────────────────────────────────
  const realCells = useMemo(() => {
    if (mode !== 'real') return [];
    const fd  = new Date(year, month, 1).getDay();
    const dim = new Date(year, month + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < fd; i++) out.push(null);
    for (let d = 1; d <= dim; d++) out.push(d);
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [mode, year, month]);

  const realActiveSel = useMemo(() => {
    if (mode !== 'real') return new Set();
    const s = new Set();
    selectedRows.forEach(r => getActiveDaysForRow(r, year, month).forEach(d => s.add(d)));
    return s;
  }, [mode, selectedRows, year, month]);

  const realActiveHov = useMemo(() => {
    if (mode !== 'real' || !hoveredRow) return new Set();
    return getActiveDaysForRow(hoveredRow, year, month);
  }, [mode, hoveredRow, year, month]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Day-of-week header */}
      <div className="cal-grid" style={{ marginBottom: 3, marginLeft: mode === 'abstract' ? 28 : 0 }}>
        {DAYS_SHORT.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, color: 'var(--muted)', paddingBottom: 3 }}>
            {d}
          </div>
        ))}
      </div>

      {mode === 'abstract' ? (
        <div style={{ position: 'relative', marginLeft: 28 }}>
          {/* Row labels on the left */}
          <div style={{
            position: 'absolute', left: -28, top: 0, bottom: 0,
            display: 'flex', flexDirection: 'column',
            justifyContent: 'space-around',
          }}>
            {ROW_LABELS.map((lbl, i) => (
              <div key={i} style={{
                fontSize: 8,
                color: i === 4 ? 'var(--accent2)' : 'var(--muted)',
                fontWeight: i === 4 ? 600 : 400,
                lineHeight: 1,
                textAlign: 'right',
                width: 24,
              }}>
                {lbl}
              </div>
            ))}
          </div>

          <div className="cal-grid" style={{ gridTemplateRows: 'repeat(5, auto)' }}>
            {abstractCells.map(({ week, dow }) => {
              const key   = week + '-' + dow;
              const isSel = activeSel.has(key);
              const isHov = activeHov.has(key) && !isSel;
              // "Last" row gets a subtle different base style
              const isLastRow = week === 4;
              const isWeekend  = dow === 0 || dow === 6;
              const dimmed     = dimWeekends && isWeekend && !isSel && !isHov;
              let cls = 'cal-cell';
              if (isSel) cls += ' active';
              if (isHov) cls += ' hovered';
              return (
                <div
                  key={key}
                  className={cls}
                  style={{
                    fontSize: 9,
                    opacity: dimmed ? 0.3 : 1,
                    outline: isLastRow && !isSel && !isHov
                      ? '1px dashed rgba(78,205,196,0.15)'
                      : undefined,
                  }}
                >
                  {DAYS_SHORT[dow]}
                </div>
              );
            })}
          </div>

          {/* Separator line before Last row */}
          <div style={{
            position: 'absolute',
            bottom: 'calc(1 * (100% / 5))',
            left: 0, right: 0,
            borderTop: '1px dashed rgba(78,205,196,0.3)',
            pointerEvents: 'none',
          }} />
        </div>
      ) : (
        <div className="cal-grid">
          {realCells.map((day, i) => {
            if (!day) return <div key={'e' + i} className="cal-cell empty" />;
            const dow    = new Date(year, month, day).getDay();
            const isWeekendDay = dow === 0 || dow === 6;
            const isSel  = realActiveSel.has(day);
            const isHov  = realActiveHov.has(day) && !isSel;
            const isToday =
              today.getFullYear() === year &&
              today.getMonth()    === month &&
              today.getDate()     === day;
            const dimmed = dimWeekends && isWeekendDay && !isSel;
            let cls = 'cal-cell';
            if (isSel)   cls += ' active';
            if (isHov)   cls += ' hovered';
            if (isToday) cls += ' today';
            return (
              <div key={day} className={cls} style={{ fontSize: 11, opacity: dimmed ? 0.3 : 1 }}>
                {day}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
